import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CloudUpload } from 'lucide-react';
import UserStatsSummary from '@/components/UserStatsSummary';
import MilestoneProgress from '@/components/MilestoneProgress';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useWelcomeTour } from '@/hooks/useWelcomeTour';
import { NetworkStrengthIndicator } from '@/components/NetworkStrengthIndicator';
import { useEffect } from 'react';

const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startTour } = useWelcomeTour();

  useEffect(() => {
    const isCompleted = localStorage.getItem('welcomeTourCompleted');
    if (isCompleted !== 'true') {
      const timer = setTimeout(() => {
        startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startTour]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-4 relative overflow-hidden">
      {/* Decorative background elements matching login page */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-emerald-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl"></div>

      {/* Top bar: network indicator pinned to top-right, never overlaps stats */}
      <div className="flex justify-end pt-3 pr-2">
        <NetworkStrengthIndicator />
      </div>

      <div className="max-w-4xl w-full mx-auto pb-24 sm:pb-8">
        {/* Stats Summary at the top */}
        <div id="tour-stats-summary">
          <UserStatsSummary />
        </div>

        <div className="text-center space-y-3 sm:space-y-5">
          {/* Swecha Logo */}
          <div className="animate-fade-in-smooth" id="tour-swecha-logo">
            <img
              src="/Swecha_Logo_English.png"
              alt={t('common.swechaTechnologyForSociety')}
              className="h-14 sm:h-20 mx-auto drop-shadow-lg hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* Milestone progress */}
          <div className="animate-fade-in-milestone flex justify-center">
            <ErrorBoundary fallback={<></>}>
              <MilestoneProgress />
            </ErrorBoundary>
          </div>

          {/* Circular Upload Button */}
          <div className="animate-fade-in-delay-smooth flex justify-center">
            <button
              id="tour-upload-btn"
              onClick={() => navigate('/upload')}
              className="group relative w-44 h-44 sm:w-60 sm:h-60 rounded-full bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-emerald-500/50 overflow-hidden"
            >
              <div className="absolute -inset-2 bg-gradient-to-br from-emerald-300 to-emerald-500 rounded-full opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2 sm:gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/20 rounded-full animate-ping-slow"></div>
                  <div
                    className="absolute inset-0 bg-white/10 rounded-full"
                    style={{
                      animation:
                        'ping-slow 4s cubic-bezier(0, 0, 0.2, 1) infinite',
                    }}
                  ></div>
                  <div className="relative bg-white/25 backdrop-blur-md rounded-full p-3 sm:p-4 group-hover:bg-white/35 transition-all duration-500 shadow-lg">
                    <CloudUpload
                      className="w-10 h-10 sm:w-14 sm:h-14 text-white animate-upload-float drop-shadow-lg"
                      strokeWidth={2.5}
                    />
                  </div>
                </div>

                <div className="space-y-0.5 sm:space-y-2">
                  <h2 className="text-lg sm:text-2xl font-bold text-white drop-shadow-md">
                    {t('common.uploadFiles')}
                  </h2>
                  <p className="text-white/80 text-xs sm:text-sm font-medium">
                    {t('common.click.to.get.started')}
                  </p>
                </div>
              </div>

              <div className="absolute inset-0 rounded-full border-4 border-white/20 group-hover:border-white/40 transition-all duration-500"></div>
            </button>
          </div>

          <p className="text-slate-500 text-sm animate-fade-in-late">
            {t('common.contributeToOpenDataAndHelpBuildTheFuture')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
